<script setup lang="ts">
import { computed, ref } from "vue";

import ColorBadge from "@/components/ui/ColorBadge.vue";
import CopyableElement from "@/components/ui/CopyableElement.vue";
import LargeSlideToggle from "@/components/ui/LargeSlideToggle.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import TabPanel from "@/components/ui/TabPanel.vue";
import TabPanelContent from "@/components/ui/TabPanelContent.vue";
import { RouteNames } from "@/router";
import type { OnboardingStepEmits } from "@/views/onboarding/types";

import condaIcon from "@/assets/icons/conda.svg";
import pythonIcon from "@/assets/icons/python.svg";
import uvIcon from "@/assets/icons/uv.svg";

const emit = defineEmits<OnboardingStepEmits>();
const { workspacePublicId } = defineProps<{
  workspacePublicId: string;
}>();

const estimatorReportSnippet = computed(
  () => `import sklearn
import skore
import skrub

skore.login()

data, target = sklearn.datasets.fetch_openml(
    data_id=42125, as_frame=True, return_X_y=True
)

data_train, data_test, target_train, target_test = (
    sklearn.model_selection.train_test_split(data, target)
)

estimator = skrub.tabular_pipeline("regressor")

report = skore.EstimatorReport(
    estimator,
    X_train=data_train,
    y_train=target_train,
    X_test=data_test,
    y_test=target_test,
)

project = skore.Project("${workspacePublicId}/my-first-project", mode="hub")
project.put("estimator", report)`
);

const estimatorReportSnippetMarkdown = computed(
  () => "```python\n" + estimatorReportSnippet.value + "\n```"
);

const crossValidationSnippet = computed(
  () => `import sklearn
import skore
import skrub

skore.login()

data, target = sklearn.datasets.fetch_openml(
    data_id=42125, as_frame=True, return_X_y=True
)

report = skore.CrossValidationReport(
    skrub.tabular_pipeline("regressor"), data, target
)

project = skore.Project("${workspacePublicId}/my-first-project", mode="hub")
project.put("cross-validation", report)`
);

const crossValidationSnippetMarkdown = computed(
  () => "```python\n" + crossValidationSnippet.value + "\n```"
);

const hasCopied = ref(false);
const isCrossValidation = ref(false);
</script>

<template>
  <div class="onboarding-put-report-step">
    <div class="card">
      <header>
        <h2 class="text-lg-medium">Upload your first report</h2>

        <h3 class="text-base-regular">
          Now push your first report to Skore Hub. Here is a minimal example to get started.
        </h3>
      </header>

      <div class="install">
        <TabPanel :uses-anchor="false" class="package-managers-tab-panel">
          <template #prefix>
            <div class="text-base-medium">Install Skore in your environment</div>
          </template>

          <TabPanelContent name="pip" :svg-icon="pythonIcon">
            <CopyableElement class="tab" text-to-copy='pip install "skore[hub]"'>
              <code>pip install "skore[hub]"</code>
            </CopyableElement>
          </TabPanelContent>

          <TabPanelContent name="conda" :svg-icon="condaIcon">
            <CopyableElement class="tab" text-to-copy="conda install skore -c conda-forge">
              <code>conda install skore -c conda-forge</code>
            </CopyableElement>
          </TabPanelContent>

          <TabPanelContent name="uv" :svg-icon="uvIcon">
            <CopyableElement class="tab" text-to-copy='uv add "skore[hub]"'>
              <code>uv add "skore[hub]"</code>
            </CopyableElement>
          </TabPanelContent>
        </TabPanel>
      </div>

      <LargeSlideToggle
        v-model:is-toggled="isCrossValidation"
        left-label="Estimator"
        left-icon="icon-cube"
        right-label="Cross-validation"
        right-icon="icon-playground"
      />

      <ColorBadge color="blue" left-icon="icon-info-circle-2">
        To avoid interactive authentication triggered by
        <code class="code-inline">skore.login()</code> we recommend that you setup an API key. Visit
        your
        <RouterLink :to="{ name: RouteNames.Account }" target="_blank">
          account settings page
        </RouterLink>
        to create a new API key and use it in your projects.
      </ColorBadge>

      <div class="snippet card">
        <header class="text-base-medium">Copy this script</header>

        <CopyableElement
          class="card-content"
          @copied="hasCopied = true"
          :text-to-copy="isCrossValidation ? crossValidationSnippet : estimatorReportSnippet"
        >
          <MarkdownWidget
            :source="
              isCrossValidation ? crossValidationSnippetMarkdown : estimatorReportSnippetMarkdown
            "
          />
        </CopyableElement>
      </div>
    </div>

    <div class="actions">
      <SimpleButton
        :label="hasCopied ? 'You are done! Continue to the hub' : 'Skip for now'"
        :icon-right="hasCopied ? 'icon-ascending-arrow' : ''"
        :kind="hasCopied ? 'primary' : 'ghost'"
        @click="emit('continue')"
      />
    </div>
  </div>
</template>

<style scoped>
.onboarding-put-report-step {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-16);

  .card {
    display: flex;
    flex-direction: column;
    padding: var(--numbers-16);
    background-color: var(--background-primary);
    gap: var(--numbers-16);

    h3 {
      color: var(--content-tertiary);
    }

    :deep(.tab-selector) {
      justify-content: flex-end;
    }

    .install {
      .tab {
        height: var(--numbers-40);
        align-items: flex-start;
        border-top: none;
        background-color: var(--background-primary);
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }

    .snippet {
      padding: var(--numbers-6);
      background-color: var(--background-secondary);
      gap: var(--numbers-8);

      header {
        margin-left: var(--numbers-12);
      }

      .card-content {
        align-items: flex-start;
        border-radius: var(--numbers-16);
      }
    }
  }

  .actions {
    display: flex;
    width: 100%;
    justify-content: space-between;
    gap: var(--numbers-16);

    .skore-button {
      flex: 1;
    }
  }
}
</style>
